import { NotificationCountType } from '../constants/matrixEvents';
import logger from './logger';
import { getCapTimeSdkCount } from './unreadBackfill';
import { getFloorEntry, setFloor } from './unreadFloor';

/**
 * Module-level cache for muted room unread counts.
 *
 * When a room is muted, the server stops counting notifications
 * (getUnreadNotificationCount returns 0). We fall back to client-side
 * calculateManualUnreadCount, but the SDK's synthesized receipt can
 * advance unpredictably during component remounts (tab switches),
 * causing calculateManualUnreadCount to also return 0.
 *
 * This cache preserves the last known non-zero unread count for muted rooms
 * so it survives component remounts. It is cleared when the user navigates
 * into the room (via clearMutedUnreadCache) or on logout.
 *
 * @type {Map<string, number>}  roomId → unreadCount
 */
const mutedUnreadCache = new Map();

/**
 * Clear all cached muted-room unread counts.
 * Called on logout to prevent cross-session data leakage.
 */
export function clearAllMutedUnreadCaches() {
  mutedUnreadCache.clear();
}

/**
 * Clear the cached unread count for a single room.
 * Called when the user navigates into a muted room (reads it).
 *
 * @param {string} roomId
 */
export function clearMutedUnreadCache(roomId) {
  mutedUnreadCache.delete(roomId);
}

/**
 * Effective read marker for unread math — returns whichever of m.fully_read
 * or the receipt read-up-to position is further ahead in the live timeline.
 *
 * m.fully_read only updates on room exit or message send, while receipt
 * local echo updates immediately via sendReadReceipt. Taking the MAX
 * ensures the badge tracks the user's actual read position as they scroll.
 *
 * @param {object} room - Matrix Room
 * @param {string} userId
 * @returns {string|null}
 */
export function getEffectiveReadMarkerEventId(room, userId) {
  if (!room || !userId) {
    return null;
  }

  let fromFullyRead = null;
  try {
    const fullyReadEvent = room.getAccountData?.('m.fully_read');
    fromFullyRead = fullyReadEvent?.getContent?.()?.event_id || null;
  } catch {
    // fall through
  }

  const fromReceipt = room.getEventReadUpTo(userId);

  // Single-marker cases
  if (!fromFullyRead) return fromReceipt;
  if (!fromReceipt) return fromFullyRead;
  if (fromFullyRead === fromReceipt) return fromFullyRead;

  // Both exist and differ — return whichever is further in the timeline.
  // This ensures receipt local echo (immediate) is preferred over stale
  // m.fully_read (which only updates on next /sync after room exit).
  const timeline = room.getLiveTimeline?.()?.getEvents?.();
  if (!timeline || timeline.length === 0) return fromFullyRead;

  const fullyReadIdx = timeline.findIndex(e => e.getId() === fromFullyRead);
  const receiptIdx = timeline.findIndex(e => e.getId() === fromReceipt);

  if (fullyReadIdx === -1 && receiptIdx === -1) return fromFullyRead;
  if (fullyReadIdx === -1) return fromReceipt;
  if (receiptIdx === -1) return fromFullyRead;

  return receiptIdx > fullyReadIdx ? fromReceipt : fromFullyRead;
}

/**
 * Count unreads after the effective read marker and whether that marker sits in the live timeline.
 *
 * `count` counts already-decrypted message/reaction events from other users.
 * `pendingDecryption` counts `m.room.encrypted` wrappers from other users that
 * haven't decrypted yet (still `m.room.encrypted` type). These represent real
 * unread messages that just haven't been processed locally; surfacing them
 * keeps the badge non-zero while decryption is in flight (NED-3439).
 *
 * @returns {{ count: number, markerTrusted: boolean, pendingDecryption: number, markerId: string | null }}
 */
function computeManualUnreadFromTimeline(room, userId) {
  if (!room || !userId) {
    return { count: 0, markerTrusted: false, pendingDecryption: 0, markerId: null };
  }

  try {
    const lastReadEventId = getEffectiveReadMarkerEventId(room, userId);

    if (!lastReadEventId) {
      logger.debug(`[calculateManualUnreadCount] Room ${room.roomId}: no read marker`);
      return { count: 0, markerTrusted: false, pendingDecryption: 0, markerId: null };
    }

    const timeline = room.getLiveTimeline().getEvents();
    const lastReadIndex = timeline.findIndex(e => e.getId() === lastReadEventId);

    if (lastReadIndex === -1) {
      logger.debug(
        `[calculateManualUnreadCount] Room ${room.roomId}: read marker ${lastReadEventId} ` +
        `not in timeline (${timeline.length} events)`,
      );
      return { count: 0, markerTrusted: false, pendingDecryption: 0, markerId: lastReadEventId };
    }

    let count = 0;
    let pendingDecryption = 0;
    for (let i = lastReadIndex + 1; i < timeline.length; i++) {
      const event = timeline[i];

      if (event.getSender() === userId) {
        continue;
      }

      if (event.isRedacted()) {
        continue;
      }

      const type = event.getType();
      if (type === 'm.room.message' || type === 'm.reaction') {
        count++;
      } else if (type === 'm.room.encrypted') {
        // Pending-decryption wrappers from another user. Always represent
        // content-level events (state events are never encrypted), so they
        // are legitimate unread candidates. Tracked separately so the
        // resolver can decide whether to surface them as a floor.
        pendingDecryption++;
      }
    }

    logger.debug(
      `[calculateManualUnreadCount] Room ${room.roomId}: count=${count}, ` +
      `pendingDecryption=${pendingDecryption}, ` +
      `readMarkerIdx=${lastReadIndex}, timelineLen=${timeline.length}`,
    );
    return { count, markerTrusted: true, pendingDecryption, markerId: lastReadEventId };
  } catch (error) {
    logger.error('[calculateManualUnreadCount] Error:', error);
    return { count: 0, markerTrusted: false, pendingDecryption: 0, markerId: null };
  }
}

/**
 * True when the read marker resolved for this (room, user) sits inside the
 * currently loaded live timeline, i.e. marker-based unread math is trustworthy.
 * False when either the marker cannot be determined or has been paginated out.
 *
 * Used by the post-refresh backfill in useRoomList/useChannels (NED-3439).
 *
 * @param {Room} room
 * @param {string} userId
 * @returns {boolean}
 */
export function isReadMarkerInTimeline(room, userId) {
  return computeManualUnreadFromTimeline(room, userId).markerTrusted;
}

/**
 * Calculate unread message count for a room using the read marker position.
 *
 * This is a pure function (not a React hook) designed to be called from useMemo
 * in useRoomList/useChannels. It counts messages after the read marker that are
 * from other users, are actual messages or reactions, and haven't been redacted.
 *
 * Used for muted rooms where the SDK's getUnreadNotificationCount() returns 0
 * because the Matrix server doesn't count notifications when push rules suppress them.
 *
 * @param {Room} room - Matrix room object
 * @param {string} userId - Current user's Matrix ID
 * @returns {number} Unread message count (0 if unable to determine)
 */
export function calculateManualUnreadCount(room, userId) {
  return computeManualUnreadFromTimeline(room, userId).count;
}

/**
 * Resolve the unread count for a room, with caching for muted rooms.
 *
 * When a room is muted the server returns sdkUnreadCount=0. We fall back to
 * calculateManualUnreadCount, but the SDK's synthesized receipt can advance
 * during component remounts (tab switches), making manual count also return 0.
 * The cache preserves the last known non-zero count to prevent badge flicker.
 *
 * @param {Room} room - Matrix room object
 * @param {string} userId - Current user's Matrix ID
 * @param {boolean} isMuted - Whether the room's push rules suppress notifications
 * @returns {number} Resolved unread count
 */
export function resolveUnreadCount(room, userId, isMuted) {
  const sdkUnreadCount = room.getUnreadNotificationCount(NotificationCountType.Total);
  const {
    count: manualCount,
    markerTrusted,
    pendingDecryption,
    markerId: resolvedMarkerId,
  } = computeManualUnreadFromTimeline(room, userId);

  if (isMuted) {
    if (sdkUnreadCount > 0) {
      mutedUnreadCache.set(room.roomId, sdkUnreadCount);
      return sdkUnreadCount;
    }
    if (manualCount > 0) {
      mutedUnreadCache.set(room.roomId, manualCount);
      return manualCount;
    }
    // Manual count is 0 — may be genuine (user read the room) or a
    // synth-receipt glitch from a tab switch. Use cached count if available.
    return mutedUnreadCache.get(room.roomId) || 0;
  }

  // Non-muted (NED-3388): when the read marker is in the live timeline, badge must follow
  // marker-based counts like useUnreadTracking inside the room. SDK highlight count often
  // lags behind receipts after a partial view, so the list would otherwise stay stuck.
  if (markerTrusted) {
    // NED-3439: pending-decryption wrappers (m.room.encrypted from other
    // users, post-marker) count as unread. Including them keeps the badge
    // accurate at cold-start when events haven't decrypted yet — without
    // this the badge would drop to 0 for the entire decryption window.
    const totalFromTimeline = manualCount + (pendingDecryption || 0);

    // Persist the trusted count as a floor so the next first-paint-after-
    // refresh is bounded from below by what we just observed, not by the
    // decryption-crawl-bounded SDK count (NED-3439 plan Workstream A).
    // Re-uses the marker already resolved inside computeManualUnreadFromTimeline.
    // The setFloor implementation enforces monotonic non-decrease within
    // an active-floor window, so partial timeline states mid-sync cannot
    // clobber a pre-refresh high-water mark.
    if (resolvedMarkerId) {
      setFloor(userId, room.roomId, totalFromTimeline, resolvedMarkerId);
    }

    // Even on the trusted path, consult the floor: post-refresh the IDB
    // store may restore the marker into the timeline so markerTrusted is
    // true, but totalFromTimeline still crawls up from 0 → N as events
    // decrypt. Returning the raw partial here defeats the floor. Take the
    // higher of the two. Floor is cleared by: (a) active view via
    // `clearFloorForRoom` in useReadReceipts, (b) room leave, or (c) a
    // fresh Receipt / m.fully_read whose ts post-dates the stored floor's
    // ts (cross-device read) via `maybeInvalidateFloorForReceipt` in the
    // hook listeners.
    const floorEntryT = getFloorEntry(userId, room.roomId);
    const floorCountT = floorEntryT ? floorEntryT.count : 0;
    const displayed = Math.max(totalFromTimeline, floorCountT);

    if (displayed > 0) {
      mutedUnreadCache.set(room.roomId, displayed);
      return displayed;
    }
    mutedUnreadCache.delete(room.roomId);
    return 0;
  }

  // Marker is not in the live timeline. We apply three fallbacks — whichever
  // is highest wins, so the badge can only stay at or above the truth:
  //   1. SDK notification count (crawls up as decryption completes).
  //   2. Cap-time snapshot (captured when backfill exhausted its cap).
  //   3. Persistent floor from the last trusted resolution.
  //
  // We deliberately do NOT invalidate the floor here based on markerId
  // equality — getEffectiveReadMarkerEventId's output depends on the live
  // timeline composition (e.g. which of fully_read/receipt is further ahead
  // in-timeline), which changes across refresh when the marker is paginated
  // out. That made the markerId check fire spurious invalidations on every
  // post-refresh first paint, defeating the entire purpose of the floor.
  // Real marker advances are instead captured by:
  //   - ts-based Receipt / m.fully_read listener invalidation in useRoomList
  //     and useChannels (receipt.ts > floor.ts → clearFloorForRoom),
  //   - active-view clearing via useReadReceipts,
  //   - fresh trusted-path writes in the markerTrusted branch above.
  const capSnapshot = getCapTimeSdkCount(room.roomId);
  const floorEntry = getFloorEntry(userId, room.roomId);
  const floorCount = floorEntry ? floorEntry.count : undefined;

  let best = sdkUnreadCount;
  if (typeof capSnapshot === 'number' && capSnapshot > best) best = capSnapshot;
  if (typeof floorCount === 'number' && floorCount > best) best = floorCount;

  if (best > 0) {
    mutedUnreadCache.set(room.roomId, best);
    return best;
  }

  mutedUnreadCache.delete(room.roomId);
  return 0;
}
